import { h } from 'preact';
import { useEffect, useState, useCallback, useRef } from 'preact/hooks';
import classNames from 'classnames';
import { IconMinusRound, IconPlusRound } from '../utils/icon';
import Image from '../utils/image';
import ProductOptionRow from './ProductOptionRow';

const ProductComponent = ({
  product,
  sectionId,
  quantityInput,
  viewDetails,
}) => {
  const hasMultipleVariants = product.variants.length > 1;
  const hasMultipleOptions = product.options.length > 1;

  const [selectedVariant, setSelectedVariant] = useState(
    () => product.selected_or_first_available_variant,
  );

  const [sizeDrawer, setSizeDrawer] = useState(() => false);
  const [initialChange, setInitialChange] = useState(false);

  const drawerRef = useRef(null);

  useEffect(() => {
    if (sizeDrawer) {
      setTimeout(() => {
        const drawerElement = drawerRef.current;
        const parentElement = document.querySelector('[data-parent-scroll]');

        if (drawerElement && parentElement) {
          const drawerRect = drawerElement.getBoundingClientRect();
          const parentRect = parentElement.getBoundingClientRect();
          const windowHeight = parentRect.height;
          // eslint-disable-next-line max-len
          const distanceFromBottom =
            windowHeight - (drawerRect.bottom - parentRect.top);

          if (distanceFromBottom < 175) {
            parentElement.scrollBy({
              top: 175 - distanceFromBottom + 5,
              behavior: 'smooth',
            });
          }
        }
      }, 100);
    }
  }, [sizeDrawer]);

  const [formOptions, setFormOptions] = useState(() => ({
    option_1: product.selected_or_first_available_variant?.option1,
    option_2: product.selected_or_first_available_variant?.option2,
    option_3: product.selected_or_first_available_variant?.option3,
    qty: 1,
  }));

  const handleSubmit = (event) => {
    event.preventDefault();

    const upsellData = product.cart_upsell ? product.cart_upsell : null;

    console.log(selectedVariant.lprop);

    localStorage.setItem('upsellProductData', JSON.stringify(upsellData));

    const attributes = [
      {
        key: '_inventory_quantity',
        value: selectedVariant.inventory_quantity,
      },
    ];

    if (selectedVariant.lprop) {
      attributes.push({
        key: '_lprop',
        value: selectedVariant.lprop,
      });
    }

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: {
          merchandiseId: `${selectedVariant.id}`,
          quantity: formOptions.qty,
          attributes,
        },
      },
    });
    document.dispatchEvent(addToCartEvent);
  };

  const getActiveVariant = useCallback(
    (event) => {
      const formData = serializeForm(event.target.closest('form'));

      if (!initialChange && event.target.id.includes('Size')) {
        setInitialChange(true);
      }

      formData.forEach((item) => {
        if (item.name.indexOf('option') !== -1) {
          setFormOptions((prevFormOptions) => ({
            ...prevFormOptions,
            [item.name]: item.value,
          }));
        }
      });
    },
    [formOptions],
  );

  useEffect(() => {
    const { variants } = product;
    const activeVariant = variants.find(
      (variant) =>
        variant.option1 === formOptions.option_1 &&
        variant.option2 === formOptions.option_2 &&
        variant.option3 === formOptions.option_3,
    );
    if (activeVariant) {
      setSelectedVariant(activeVariant);
    }
  }, [formOptions, product]);

  const serializeForm = (form) => {
    const formData = new FormData(form);
    const serializedArray = [...formData.entries()].map(([name, value]) => ({
      name,
      value,
    }));
    return serializedArray;
  };

  const handleQuantityChange = (event) => {
    const { value } = event.target;
    setFormOptions((prevFormOptions) => ({
      ...prevFormOptions,
      qty: value,
    }));
  };

  return (
    <div className="mb-24 last-of-type:mb-0 sm:mb-32">
      <form
        action="/cart/add"
        method="post"
        encType="multipart/form-data"
        id="react-add-to-cart-form"
        onSubmit={handleSubmit}
        onChange={getActiveVariant}
      >
        <div className="grid grid-cols-[117px_1fr] gap-16">
          <div className="relative aspect-[117/146]">
            <Image
              src={selectedVariant.featured_image?.src}
              alt={selectedVariant.featured_image?.alt}
              srcWidths={{ sm: 300, lg: 300 }}
              classes="cover absolute inset-0 block h-full w-full object-cover object-center"
            />
          </div>
          <div className="flex flex-col justify-between">
            <div>
              <h2 className="cta-medium mb-4">{product.title}</h2>

              <span
                className="body-2-bold mb-4 block"
                data-default-price={selectedVariant.price}
              >
                <span
                  className={classNames('body-2-bold', {
                    'text-accentColor-2': selectedVariant.onSale,
                  })}
                >
                  {selectedVariant.price_formatted}
                </span>
                {selectedVariant.compare_at_price ? (
                  <span className="body-3 text-gray-800">
                    {' '}
                    | {selectedVariant.compare_price_formatted} Comp. Value
                  </span>
                ) : null}
              </span>

              {hasMultipleOptions || hasMultipleVariants
                ? product?.options_with_values
                    .filter((option) => option.name === 'Color')
                    .map((option, index) => (
                      <ProductOptionRow
                        key={index}
                        sectionId={sectionId}
                        productId={product.id}
                        sizeGuide={product.size_guide}
                        initialChange={initialChange}
                        hideSizeGuide={true}
                        position={`option1`}
                        option={option}
                        productVariants={product.variants}
                        formOptions={formOptions}
                        setFormOptions={setFormOptions}
                      />
                    ))
                : null}
            </div>
            {viewDetails === 'false' ? null : (
              <div className="mb-8">
                <a
                  className="button-tertiary"
                  href={selectedVariant.url}
                >
                  View Details
                </a>
              </div>
            )}
            <div>
              <button
                className="body-2 flex w-full max-w-[180px] items-center justify-center gap-x-15 rounded-[20px] border border-gray-600 bg-inverse px-15 py-10 md:max-w-[175px]"
                type="button"
                onClick={() => setSizeDrawer((prevState) => !prevState)}
              >
                Select Size
                {sizeDrawer ? (
                  <IconMinusRound className="w-15" />
                ) : (
                  <IconPlusRound className="h-15 w-15" />
                )}
              </button>
            </div>
          </div>
        </div>

        <div
          ref={drawerRef}
          className={classNames(
            'transition-all',
            sizeDrawer
              ? 'mt-16 max-h-[500px] overflow-visible'
              : 'max-h-0 overflow-hidden',
          )}
        >
          {hasMultipleOptions || hasMultipleVariants
            ? product?.options_with_values
                .filter((option) => option.name === 'Size')
                .map((option, index) => (
                  <ProductOptionRow
                    fitText={product.fit_text}
                    key={index}
                    sizeGuide={product.size_guide}
                    hideSizeGuide={false}
                    sectionId={sectionId}
                    productId={product.id}
                    position={`option2`}
                    option={option}
                    productVariants={product.variants}
                    formOptions={formOptions}
                    setFormOptions={setFormOptions}
                  />
                ))
            : null}

          <input
            className="js-variant-id"
            type="hidden"
            name="id"
            value={selectedVariant.id}
          />

          <label
            className={classNames(quantityInput === 'false' ? 'hidden' : null)}
            htmlFor="Quantity"
          >
            Quantity
          </label>
          <input
            className={classNames(quantityInput === 'false' ? 'hidden' : null)}
            type="number"
            id="Quantity"
            name="quantity"
            value={formOptions.qty}
            min="1"
            max={selectedVariant.inventory_quantity}
            onChange={handleQuantityChange}
          />
          <button
            type="submit"
            className="button-primary-inverse mt-16 w-full"
            name="add"
            id="react-add-to-cart-button"
            disabled={!selectedVariant.available}
          >
            Add to cart
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductComponent;
